/**
 * Onclicka Ads Integration Hook
 * ID: 6103374
 * 
 * Supports:
 * - Rewarded In-Stream Ads
 * 
 * Integration Guide:
 * - SDK loads from https://js.onclckvd.com/in-stream-ad-admanager/tma.js
 * - Initialize with window.initCdTma?.({ id: 6103374 })
 * - Show function returns Promise<void> on success, throws on failure
 */

import { useConfigStore } from '../store/useConfigStore';

declare global {
  interface Window {
    initCdTma?: (options: { id: number }) => Promise<() => Promise<void>>;
    showOnclickaAd?: () => Promise<void>;
    onclickaShowFunction?: () => Promise<void>;
    onclickaInitialized?: boolean;
  }
}

// Store the show function globally after initialization
let onclickaShowFn: (() => Promise<void>) | null = null;
let initializationPromise: Promise<(() => Promise<void>) | null> | null = null;

// Check if Onclicka is enabled
function isOnclickaEnabled(): boolean {
  const config = useConfigStore.getState();
  return config.adsEnabled && config.onclicka.enabled;
}

/**
 * Initialize Onclicka SDK and get the show function
 * Called once and cached for subsequent calls
 */
async function initOnclicka(): Promise<(() => Promise<void>) | null> {
  // Return cached show function if already initialized
  if (onclickaShowFn) {
    return onclickaShowFn;
  }

  // Return existing initialization promise if in progress
  if (initializationPromise) {
    return initializationPromise;
  }

  // Start initialization
  initializationPromise = (async () => {
    try {
      const config = useConfigStore.getState();
      const adId = config.onclicka.adId || 6103374;

      if (!window.initCdTma) {
        console.error('Onclicka SDK not loaded');
        return null;
      }

      const showFn = await window.initCdTma({ id: adId });
      onclickaShowFn = showFn;
      window.onclickaShowFunction = showFn;
      window.onclickaInitialized = true;
      
      console.log('Onclicka SDK initialized successfully');
      return showFn;
    } catch (error) {
      console.error('Failed to initialize Onclicka SDK:', error);
      return null;
    }
  })();

  return initializationPromise;
}

/**
 * Show Onclicka Rewarded Ad
 * Returns true if ad was shown successfully, false otherwise
 */
export async function showOnclickaAd(ymid?: string): Promise<boolean> {
  if (!isOnclickaEnabled()) {
    console.warn('Onclicka ads are disabled');
    return false;
  }

  try {
    // Ensure SDK is initialized
    const showFn = await initOnclicka();
    
    if (!showFn) {
      console.error('Onclicka show function not available');
      return false;
    }

    // Show the ad - promise resolves on success, rejects on failure
    await showFn();
    console.log('Onclicka ad shown successfully', ymid ? `(${ymid})` : '');
    return true;
  } catch (error) {
    console.error('Onclicka ad failed:', error);
    return false;
  }
}

/**
 * Show Onclicka Rewarded Interstitial Ad
 * Alias for showOnclickaAd for compatibility
 */
export async function showOnclickaRewardedInterstitial(ymid?: string): Promise<boolean> {
  return showOnclickaAd(ymid);
}

/**
 * Show Onclicka Rewarded Popup Ad
 * Alias for showOnclickaAd for compatibility (Onclicka uses same flow)
 */
export async function showOnclickaRewardedPopup(ymid?: string): Promise<boolean> {
  return showOnclickaAd(ymid);
}

/**
 * Preload Onclicka ad for faster display
 * Just initializes the SDK if not already done
 */
export async function preloadOnclickaAd(ymid?: string): Promise<boolean> {
  if (!isOnclickaEnabled()) {
    return false;
  }

  try {
    const showFn = await initOnclicka();
    console.log('Onclicka preloaded', ymid ? `(${ymid})` : '');
    return showFn !== null;
  } catch {
    return false;
  }
}

/**
 * Check if Onclicka SDK is loaded
 */
export function isOnclickaLoaded(): boolean {
  return typeof window.initCdTma === 'function';
}

/**
 * Check if Onclicka is preloaded/initialized
 */
export function isOnclickaPreloaded(): boolean {
  return window.onclickaInitialized === true && onclickaShowFn !== null;
}

/**
 * React hook for Onclicka ads
 * Returns show function that can be called on button click
 */
export function useOnclicka() {
  const config = useConfigStore((state) => state.onclicka);
  const adsEnabled = useConfigStore((state) => state.adsEnabled);

  const showAd = async (): Promise<boolean> => {
    if (!adsEnabled || !config.enabled) {
      console.warn('Onclicka ads are disabled');
      return false;
    }
    return showOnclickaAd();
  };

  return {
    showAd,
    isEnabled: adsEnabled && config.enabled,
    reward: config.rewardAmount || 100,
    dailyLimit: config.dailyLimit || 25,
  };
}
